import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useAuth } from '../views/parent/EsAuth';
import { API_ACCOUNT_LOCALE } from './EsConstants';
import { CRAFTER_LOCALES } from './EsCrafter';
import { esCopyLoad, esCopyLocales, useEsCopy } from './EsCopy';
import { esPut, esSetFetchLocale } from './EsFetch';
import { useEsTranslationsFlag, useEsFlagsReady } from './EsFlags';

/*
 * Which locale a reader reads escapade in: a canonical BCP-47 tag (en-US, fr, zh-Hant-TW).
 *
 * The account's own choice (es_account.locale, from /authUser) when signed in; else what this
 * browser picked (localStorage); else the first of the browser's languages escapade has (Breton
 * then French reads French); else en-US.
 * An anonymous reader's pick lives in localStorage, the one place a choice made before signing
 * in can live, and is written to the account at the next pick signed in.
 *
 * "Escapade has it" depends on rolloutEsTranslations. On, the browser's tag counts when it resolves
 * to a live group (GET /api/v1/copy/locales), and this provider loads that locale's bundle and
 * sets lang and dir on <html>. Off, it counts when Crafter has its language, as before copykeys,
 * nothing asks esback for copy, and <html> is left alone.
 *
 * NO ENGLISH THAT IS ABOUT TO CHANGE. Every literal is English, so a page drawn before its bundle
 * lands reads English and then turns. While a reader's locale is not English and its bundle has
 * not answered, <html> is hidden: on first load only, and never longer than LOCALE_HOLD_MS, so an
 * esback that does not answer costs a moment and then the English literals. The live list and the
 * last tag read are remembered in this browser (LOCALE_HINT_KEY), so the next visit asks for its
 * bundle without waiting for the list, and holds even before LaunchDarkly has answered. The hint
 * is written only while copykeys is on and dropped once the flag answers off, so flag-off never
 * holds.
 */

export const LOCALE_STORAGE_KEY = 'es.locale';
export const LOCALE_FALLBACK = 'en-US';
export const LOCALE_HINT_KEY = 'es.locale.hint';
export const LOCALE_HOLD_MS = 2500;

const LocaleContext = createContext( null );

/** pValue as a canonical tag (EN_us -> en-US), or null when it is not one. */
export function esLocaleOf( pValue ) {
    try {
        return Intl.getCanonicalLocales( String( pValue ?? '' ).replace( /_/g, '-' ) )[ 0 ] ?? null;
    }
    catch {
        return null;
    }
}

export const esLanguageOf = ( pTag ) => String( pTag ?? '' ).split( '-' )[ 0 ].toLowerCase();

/**
 * The first of pKeys that pTag reads, or null: likely subtags filled in (pt is pt-Latn-BR,
 * zh-TW is zh-Hant-TW), then lang-Script-Region, lang-Region, lang-Script, lang.
 *
 * Only to mark the menu's row before the bundle answers, and to decide whether a browser's tag
 * is one escapade has. esback's resolution is the answer -- it also knows macro-regions (es-MX
 * reads es-419) -- and the bundle's chain corrects this.
 */
export function esResolveLocale( pTag, pKeys ) {
    const aTag = esLocaleOf( pTag );
    if ( !aTag || !pKeys?.length ) return null;

    const aKeys = new Map( pKeys.map( ( pKey ) => [ String( esLocaleOf( pKey ) ).toLowerCase(), pKey ] ) );
    let aFull;
    try {
        aFull = new Intl.Locale( aTag ).maximize();
    }
    catch {
        aFull = { language: esLanguageOf( aTag ) };
    }
    const { language, script, region } = aFull;
    const aTries = [ aTag,
        script && region && `${ language }-${ script }-${ region }`,
        region && `${ language }-${ region }`,
        script && `${ language }-${ script }`,
        language ];

    for ( const aTry of aTries ) {
        const aKey = aTry && aKeys.get( aTry.toLowerCase() );
        if ( aKey ) return aKey;
    }
    return null;
}

/** The group a reader is shown as reading: the first live one along the bundle's chain. */
export function esCurrentLocale( pChain, pLiveKeys ) {
    const aLive = new Set( pLiveKeys );
    return ( pChain ?? [] ).find( ( pKey ) => aLive.has( pKey ) ) ?? null;
}

function readStored() {
    try {
        return esLocaleOf( window.localStorage.getItem( LOCALE_STORAGE_KEY ) );
    }
    catch {
        return null;
    }
}

function writeStored( pLocale ) {
    try {
        window.localStorage.setItem( LOCALE_STORAGE_KEY, pLocale );
    }
    catch {
        // A browser refusing storage keeps the pick for this page only.
    }
}

function readHint() {
    try {
        const aHint = JSON.parse( window.localStorage.getItem( LOCALE_HINT_KEY ) );
        return aHint && typeof aHint === 'object'
            ? { live: Array.isArray( aHint.live ) ? aHint.live : null, tag: esLocaleOf( aHint.tag ) }
            : null;
    }
    catch {
        return null;
    }
}

function writeHint( pHint ) {
    try {
        if ( pHint ) window.localStorage.setItem( LOCALE_HINT_KEY, JSON.stringify( pHint ) );
        else window.localStorage.removeItem( LOCALE_HINT_KEY );
    }
    catch {
        // A browser refusing storage asks for the list first next time, and never holds.
    }
}

/** The browser's languages, most preferred first, canonical: navigator.languages, else navigator.language. */
export function esBrowserTags( pNavigator = typeof navigator === 'undefined' ? null : navigator ) {
    const aRaw = pNavigator?.languages?.length ? pNavigator.languages : [ pNavigator?.language ];
    return [ ...new Set( aRaw.map( esLocaleOf ).filter( Boolean ) ) ];
}

export function EsLocaleProvider( { children } ) {
    const auth = useAuth();
    const aOn = useEsTranslationsFlag();
    const aCopy = useEsCopy();
    const [ stored, setStored ] = useState( readStored );
    const [ chosen, setChosen ] = useState( null );
    const [ locales, setLocales ] = useState( null );
    const [ hint ] = useState( readHint );
    const aFlags = useEsFlagsReady();

    useEffect( () => {
        if ( !aOn ) return undefined;
        let aLive = true;
        esCopyLocales().then( ( pList ) => { if ( aLive ) setLocales( pList ); } );
        return () => { aLive = false; };
    }, [ aOn ] );

    // The list esback answers, else the one this browser remembers from last time.
    const aLiveKeys = useMemo( () => ( locales
        ? locales.filter( ( pGroup ) => pGroup.live ).map( ( pGroup ) => pGroup.key )
        : hint?.live ?? [] ), [ locales, hint ] );

    // Flag off reads navigator.language alone, as it did before copykeys.
    const aBrowserTags = esBrowserTags();
    const aBrowserPick = aOn
        ? aBrowserTags.find( ( pTag ) => esResolveLocale( pTag, aLiveKeys ) ) ?? null
        : CRAFTER_LOCALES.includes( esLanguageOf( aBrowserTags[ 0 ] ) ) ? aBrowserTags[ 0 ] : null;

    const aPicked = chosen ?? esLocaleOf( auth?.account?.locale ) ?? stored;
    const aLocale = aPicked ?? aBrowserPick ?? LOCALE_FALLBACK;

    // Set while rendering, not in an effect: children's effects run first and fetch first. Only a
    // pick is sent; the browser's own tag already goes as Accept-Language, which esback resolves.
    esSetFetchLocale( aOn ? aPicked : null );

    // Whose bundle: esback shows admin keys to admins only, so a sign-in asks afresh.
    const aWho = auth?.authenticated ? String( auth?.account?.accountId ?? 'member' ) + ( auth?.admin ? ':admin' : '' ) : null;

    // Waits for /authUser, whose account may carry a locale, and -- for a reader who has picked
    // nothing -- for the locales list, which decides whether the browser's tag counts: a French
    // browser loads fr once rather than en-US and then fr.
    const aSettled = aOn && !auth?.loading && ( aPicked !== null || locales !== null || Boolean( hint?.live ) );

    useEffect( () => {
        if ( aSettled ) esCopyLoad( aLocale, { who: aWho } );
    }, [ aSettled, aLocale, aWho ] );

    const aCurrent = aOn
        ? ( aCopy.loaded ? esCurrentLocale( aCopy.chain, aLiveKeys ) : null ) ?? esResolveLocale( aLocale, aLiveKeys ) ?? LOCALE_FALLBACK
        : aLocale;
    const aGroup = ( locales ?? [] ).find( ( pGroup ) => pGroup.key === ( aCopy.group ?? aCurrent ) );

    useEffect( () => {
        if ( !aOn ) return;
        document.documentElement.lang = aCopy.group ?? aCurrent;
        document.documentElement.dir = aGroup?.rtl ? 'rtl' : 'ltr';
    }, [ aOn, aCopy.group, aCurrent, aGroup ] );

    // The hint: written only while copykeys is on, dropped once the flag has answered off.
    useEffect( () => {
        if ( aOn ) {
            if ( locales || aCopy.loaded ) writeHint( { live: aLiveKeys, tag: aCopy.loaded ? aCopy.tag : hint?.tag ?? null } );
        }
        else if ( aFlags.mayRedirect ) writeHint( null );
    }, [ aOn, aFlags.mayRedirect, locales, aLiveKeys, aCopy.loaded, aCopy.tag, hint ] );

    // The locale the first paint will turn into: the pick; before /authUser, the last tag read
    // here; the browser's, and before the list answers its first language at all. Before
    // LaunchDarkly answers for THIS reader -- mayRedirect, not answered, which is true once it
    // has answered for the anonymous context it starts on -- the last tag read with copykeys on.
    const aComing = aOn
        ? aPicked ?? ( auth?.loading ? hint?.tag : null ) ?? aBrowserPick
            ?? ( locales === null ? aBrowserTags[ 0 ] : null ) ?? LOCALE_FALLBACK
        : !aFlags.mayRedirect ? hint?.tag ?? null : null;
    const aWaiting = Boolean( aComing ) && esLanguageOf( aComing ) !== esLanguageOf( LOCALE_FALLBACK )
        && !( aCopy.loaded && aCopy.tag === aComing );
    const [ released, setReleased ] = useState( false );
    const aHold = aWaiting && !released;

    useEffect( () => {
        if ( released ) return undefined;
        if ( !aWaiting ) {
            setReleased( true );
            return undefined;
        }
        const aDeadline = setTimeout( () => setReleased( true ), LOCALE_HOLD_MS );
        return () => clearTimeout( aDeadline );
    }, [ released, aWaiting ] );

    useLayoutEffect( () => {
        if ( !aHold ) return undefined;
        const aStyle = document.documentElement.style;
        aStyle.visibility = 'hidden';
        return () => aStyle.removeProperty( 'visibility' );
    }, [ aHold ] );

    const choose = useCallback( ( pLocale ) => {
        const aPicked = esLocaleOf( pLocale ) ?? LOCALE_FALLBACK;
        writeStored( aPicked );
        setStored( aPicked );
        setChosen( aPicked );
        return aPicked;
    }, [] );

    const aValue = useMemo( () => ( { locale: aLocale, current: aCurrent, locales, choose } ),
        [ aLocale, aCurrent, locales, choose ] );

    return <LocaleContext.Provider value={ aValue }>{ children }</LocaleContext.Provider>;
}

/** The reader's locale; en-US outside a provider, which is every unit test that mounts none. */
export const useEsLocale = () => useContext( LocaleContext )?.locale ?? LOCALE_FALLBACK;

/** The live group the reader is shown as reading, for the menu's marked row. */
export const useEsCurrentLocale = () => useContext( LocaleContext )?.current ?? LOCALE_FALLBACK;

/** Every locale group esback lists, live or not; null until it answers or while copykeys is off. */
export const useEsLocales = () => useContext( LocaleContext )?.locales ?? null;

/** Picks a locale for this reader; a no-op outside a provider. */
export const useEsLocaleChooser = () => useContext( LocaleContext )?.choose ?? ( ( pLocale ) => pLocale );

/**
 * The one way a reader picks: the nav's menu and the account page both call it. Signed in,
 * the pick is saved to the account too, so esback mails them in it; signed out it is this
 * browser's.
 */
export function useEsLocalePick() {
    const auth = useAuth();
    const choose = useEsLocaleChooser();
    const aAuthenticated = Boolean( auth?.authenticated );

    return useCallback( ( pLocale ) => {
        const aPicked = choose( pLocale );
        if ( aAuthenticated ) esPut( API_ACCOUNT_LOCALE, { locale: aPicked } ).catch( () => null );
        return aPicked;
    }, [ choose, aAuthenticated ] );
}
